import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "../utils/axios";
import { AuthContext } from "../context/AuthContext";

// Keep your global skin if you need it, but this component’s styles are scoped
// import "./styles/ReportViewer.css";
import "./styles/ForecastReportViewer.css";

// Charts
import {
  Chart as ChartJS,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";

ChartJS.register(
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Title
);

export default function ForecastReportViewer() {
  const { user, loading, token } = useContext(AuthContext);

  const [masterSites, setMasterSites] = useState([]);
  const [selectedMasterSiteId, setSelectedMasterSiteId] = useState("");

  const [dailyForecast, setDailyForecast] = useState({ history: [], forecast: [], unit: "L" });
  const [monthlyForecast, setMonthlyForecast] = useState({ history: [], forecast: [], unit: "L" });

  const [errDaily, setErrDaily] = useState("");
  const [errMonthly, setErrMonthly] = useState("");
  const [busy, setBusy] = useState(false);

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const fetchMasterSites = async () => {
    try {
      const res = await axios.get("/mastersites", authHeader);
      const filtered =
        user?.role === "admin"
          ? res.data
          : res.data.filter((ms) => (user?.masterSiteAccess || []).includes(ms._id));
      setMasterSites(filtered);
      if (filtered.length > 0) setSelectedMasterSiteId(filtered[0]._id);
    } catch (e) {
      console.error("mastersites error:", e);
    }
  };

  const fetchDailyForecast = async (id) => {
    setErrDaily("");
    try {
      const res = await axios.get(`/forecast/daily/master/${id}?h=7`, authHeader);
      setDailyForecast(res.data || { history: [], forecast: [], unit: "L" });
    } catch (e) {
      console.error("daily forecast error:", e);
      setErrDaily(e?.response?.data?.message || "Failed to load daily forecast");
      setDailyForecast({ history: [], forecast: [], unit: "L" });
    }
  };

  const fetchMonthlyForecast = async (id) => {
    setErrMonthly("");
    try {
      const res = await axios.get(`/forecast/monthly/master/${id}?months=1`, authHeader);
      setMonthlyForecast(res.data || { history: [], forecast: [], unit: "L" });
    } catch (e) {
      console.error("monthly forecast error:", e);
      setErrMonthly(e?.response?.data?.message || "Failed to load monthly forecast");
      setMonthlyForecast({ history: [], forecast: [], unit: "L" });
    }
  };

  useEffect(() => {
    if (user) fetchMasterSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!selectedMasterSiteId) return;
    (async () => {
      setBusy(true);
      await Promise.all([
        fetchDailyForecast(selectedMasterSiteId),
        fetchMonthlyForecast(selectedMasterSiteId),
      ]);
      setBusy(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMasterSiteId]);

  // ---------- Chart helpers ----------
  const formatIN = (n, digits = 2) =>
    new Intl.NumberFormat("en-IN", { maximumFractionDigits: digits }).format(Number(n || 0));

  const makeGradient = (ctx, c1, c2, vertical = true) => {
    const chart = ctx.chart;
    const { ctx: c, chartArea } = chart || {};
    if (!chartArea) return c1;
    const g = vertical
      ? c.createLinearGradient(0, chartArea.bottom, 0, chartArea.top)
      : c.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
    g.addColorStop(0, c1);
    g.addColorStop(1, c2);
    return g;
  };

  const dailyChartData = {
    labels: [
      ...dailyForecast.history.map((x) => x.dateStr),
      ...dailyForecast.forecast.map((x) => x.dateStr),
    ],
    datasets: [
      {
        type: "line",
        label: "History (Last 30 days)",
        data: dailyForecast.history.map((x) => x.value),
        borderWidth: 2,
        pointRadius: 0,
        segment: { borderDash: [] },
        borderColor: (ctx) => makeGradient(ctx, "#93C5FD", "#1D4ED8"),
        backgroundColor: "transparent",
        tension: 0.3,
      },
      {
        type: "line",
        label: "Forecast (Next 7 days)",
        data: [
          ...Array(dailyForecast.history.length).fill(null),
          ...dailyForecast.forecast.map((x) => x.value),
        ],
        borderWidth: 2,
        pointRadius: 3,
        borderDash: [6, 6],
        borderColor: (ctx) => makeGradient(ctx, "#FDBA74", "#C2410C"),
        backgroundColor: "transparent",
        tension: 0.3,
      },
    ],
  };

  const dailyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: 8 },
    animation: { duration: 800, easing: "easeOutQuart" },
    plugins: {
      title: {
        display: true,
        text: "Master Total — Daily History & 7-Day Forecast",
        font: { size: 16, weight: "600" },
      },
      legend: { position: "top", labels: { usePointStyle: true, boxWidth: 8 } },
      tooltip: {
        padding: 10,
        callbacks: {
          label: (ctx) =>
            ` ${ctx.dataset.label}: ${formatIN(ctx.parsed.y)} ${dailyForecast.unit || "L"}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true } },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.06)", borderDash: [4, 4] },
        ticks: {
          callback: (v) => {
            const n = Number(v);
            if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
            if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
            return `${n}`;
          },
        },
      },
    },
  };

  const monthlyLabels = [
    ...monthlyForecast.history.map((x) => x.month),
    ...monthlyForecast.forecast.map((x) => x.month),
  ];
  const monthlyHistoryValues = [
    ...monthlyForecast.history.map((x) => x.value),
    ...Array(monthlyForecast.forecast.length).fill(null),
  ];
  const monthlyForecastValues = [
    ...Array(monthlyForecast.history.length).fill(null),
    ...monthlyForecast.forecast.map((x) => x.value),
  ];

  const monthlyChartData = {
    labels: monthlyLabels,
    datasets: [
      {
        type: "bar",
        label: "History (Monthly Total)",
        data: monthlyHistoryValues,
        backgroundColor: (ctx) => makeGradient(ctx, "#A7F3D0", "#10B981"),
        borderColor: "#10B981",
        borderWidth: 1,
        borderRadius: 10,
        borderSkipped: false,
        maxBarThickness: 42,
      },
      {
        type: "bar",
        label: "Forecast (Next Month)",
        data: monthlyForecastValues,
        backgroundColor: (ctx) => makeGradient(ctx, "#FDE68A", "#F59E0B"),
        borderColor: "#F59E0B",
        borderWidth: 1,
        borderRadius: 10,
        borderSkipped: false,
        maxBarThickness: 42,
      },
    ],
  };

  const monthlyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: 8 },
    animation: { duration: 800, easing: "easeOutQuart" },
    plugins: {
      title: {
        display: true,
        text: "Master Total — Monthly History & Forecast",
        font: { size: 16, weight: "600" },
      },
      legend: { position: "top", labels: { usePointStyle: true, boxWidth: 8 } },
      tooltip: {
        padding: 10,
        callbacks: {
          label: (ctx) =>
            ` ${ctx.dataset.label}: ${formatIN(ctx.parsed.y)} ${monthlyForecast.unit || "L"}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true } },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.06)", borderDash: [4, 4] },
        ticks: {
          callback: (v) => {
            const n = Number(v);
            if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
            if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
            return `${n}`;
          },
        },
      },
    },
  };

  if (loading) return <p>Loading user...</p>;
  if (!user) return <p>🔒 Not logged in</p>;

  return (
    <div className="forecast-container report-viewer-container">
      <div className="header-row">
        <h2 className="report-heading">Forecasts — Master Totals</h2>
      </div>

      <div className="control-block">
        <label className="control-label">Select Master Site:</label>
        <select
          className="control-select"
          value={selectedMasterSiteId}
          onChange={(e) => setSelectedMasterSiteId(e.target.value)}
        >
          {masterSites.map((site) => (
            <option key={site._id} value={site._id}>
              {site.name}
            </option>
          ))}
        </select>
      </div>

      {busy && <p className="muted">Loading forecasts…</p>}

      <div className="charts-grid">
        <div className="chart-card">
          <Line data={dailyChartData} options={dailyChartOptions} />
        </div>
        {errDaily ? (
          <p className="error-text mt-2">{errDaily}</p>
        ) : !dailyForecast?.history?.length && !dailyForecast?.forecast?.length ? (
          <p className="muted mt-2">No daily data available.</p>
        ) : null}

        <div className="chart-card">
          <Bar data={monthlyChartData} options={monthlyChartOptions} />
        </div>
        {errMonthly ? (
          <p className="error-text mt-2">{errMonthly}</p>
        ) : !monthlyForecast?.history?.length && !monthlyForecast?.forecast?.length ? (
          <p className="muted mt-2">No monthly data available.</p>
        ) : null}
      </div>
    </div>
  );
}
